if (!customElements.get('inrama-hero-carousel')) {
  class InramaHeroCarousel extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('[data-hero-viewport]');
      this.slides = Array.from(this.querySelectorAll('[data-hero-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-hero-dot]'));
      this.progress = this.querySelector('[data-hero-progress]');
      this.progressFills = Array.from(this.querySelectorAll('[data-hero-progress-fill]'));
      this.pauseBtn = this.querySelector('[data-hero-pause]');

      this.index = Math.max(
        0,
        this.slides.findIndex((slide) => slide.classList.contains('is-active'))
      );
      if (this.index < 0) this.index = 0;

      this.autoplayEnabled = this.dataset.autoplay === 'true';
      this.speed = Math.max(1, Number(this.dataset.speed) || 8) * 1000;
      this.loop = this.dataset.loop !== 'false';
      this.pauseOnHover = this.dataset.pauseOnHover !== 'false';
      this.pauseOnFocus = this.dataset.pauseOnFocus !== 'false';
      this.respectReducedMotion = this.dataset.reducedMotion !== 'false';
      this.timer = null;
      this.startedAt = 0;
      this.remaining = this.speed;
      this.paused = false;
      this.userPaused = false;

      if (this.slides.length < 2) return;

      // Rendered paused so the bar cannot drift ahead of the timer before JS boots.
      this.progress?.classList.toggle('is-static', !this.autoplayEnabled || this.prefersReducedMotion());

      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => this.goTo(Number(dot.dataset.heroDot)));
      });
      this.pauseBtn?.addEventListener('click', () => this.toggleUserPause());

      this.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.goTo(this.index + 1);
        } else if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.goTo(this.index - 1);
        }
      });

      if (this.pauseOnHover) {
        this.addEventListener('mouseenter', () => this.pause());
        this.addEventListener('mouseleave', () => this.resume());
      }

      if (this.pauseOnFocus) {
        this.addEventListener('focusin', () => this.pause());
        this.addEventListener('focusout', (event) => {
          if (!this.contains(event.relatedTarget)) this.resume();
        });
      }

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) this.resume();
            else this.pause();
          });
        }, { threshold: 0.35 });
        this.observer.observe(this);
      }

      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
      document.addEventListener('shopify:section:select', this.onSectionSelect);

      this.goTo(this.index, { instant: true });
      this.classList.add('is-ready');
      this.start();
    }

    disconnectedCallback() {
      this.stop();
      this.observer?.disconnect();
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
      document.removeEventListener('shopify:section:select', this.onSectionSelect);
    }

    onBlockSelect = (event) => {
      const slide = event.target.closest?.('[data-hero-slide]') || event.target.querySelector?.('[data-hero-slide]');
      if (!slide || !this.contains(slide)) return;
      const index = this.slides.indexOf(slide);
      if (index < 0) return;
      this.pause();
      this.goTo(index, { instant: true });
    };

    onBlockDeselect = (event) => {
      if (!this.contains(event.target)) return;
      this.resume();
    };

    onSectionSelect = () => {
      this.resume();
    };

    prefersReducedMotion() {
      return this.respectReducedMotion && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    start() {
      if (!this.autoplayEnabled || this.prefersReducedMotion() || this.userPaused) return;
      this.stop();
      this.startedAt = Date.now();
      this.timer = window.setTimeout(() => this.goTo(this.index + 1), this.remaining);
      this.progress?.classList.remove('is-paused');
    }

    stop() {
      if (this.timer) {
        window.clearTimeout(this.timer);
        this.timer = null;
      }
    }

    pause() {
      // Bank the unspent time so resuming lines the timer back up with the
      // progress bar, which resumes from wherever it was paused.
      if (this.timer) {
        this.remaining = Math.max(0, this.remaining - (Date.now() - this.startedAt));
      }
      this.paused = true;
      this.stop();
      this.progress?.classList.add('is-paused');
    }

    resume() {
      if (this.userPaused) return;
      this.paused = false;
      this.start();
    }

    toggleUserPause() {
      this.userPaused = !this.userPaused;
      this.pauseBtn?.setAttribute('aria-pressed', String(this.userPaused));
      this.pauseBtn?.setAttribute('aria-label', this.userPaused ? 'Play slideshow' : 'Pause slideshow');
      this.pauseBtn?.classList.toggle('is-paused', this.userPaused);
      if (this.userPaused) this.pause();
      else this.resume();
    }

    goTo(nextIndex, { instant = false } = {}) {
      if (!this.slides.length) return;

      let index = nextIndex;
      if (this.loop) {
        index = (index + this.slides.length) % this.slides.length;
      } else {
        index = Math.min(this.slides.length - 1, Math.max(0, index));
      }

      this.slides.forEach((slide, i) => {
        const active = i === index;
        slide.classList.toggle('is-active', active);
        slide.toggleAttribute('aria-hidden', !active);
      });

      this.dots.forEach((dot, i) => {
        const active = i === index;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-selected', String(active));
      });

      this.restartProgress(index);

      if (this.dataset.transition === 'slide' && this.viewport) {
        this.viewport.scrollTo({
          left: this.slides[index].offsetLeft,
          behavior: instant ? 'auto' : 'smooth'
        });
      }

      this.index = index;
      this.remaining = this.speed;
      if (!this.paused && !this.userPaused) this.start();
    }

    // The fill is a CSS animation, so it needs a reflow between removal and
    // re-application to replay when the same bar is re-selected.
    restartProgress(index) {
      const fill = this.progressFills[index];
      if (!fill) return;
      fill.style.animation = 'none';
      void fill.offsetWidth;
      fill.style.animation = '';
    }
  }

  customElements.define('inrama-hero-carousel', InramaHeroCarousel);
}
